/**
 * Enhanced Charge Flow
 * Handles advanced charge creation with saved payment methods
 * Includes inventory review modal integration for anode management
 */

import { getTodayLocal } from '../../utils/date-utils.js';
import { openInventoryReviewModal } from '../inventory-review-modal.js';

/**
 * Helper function to extract YouTube playlist ID from various URL formats
 * @param {string} url - YouTube playlist URL or ID
 * @returns {string|null} - Playlist ID or null if not found
 */
function extractPlaylistId(url) {
  if (!url) return null;
  const patterns = [
    /[?&]list=([a-zA-Z0-9_-]+)/,
    /playlist\/([a-zA-Z0-9_-]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  if (/^[a-zA-Z0-9_-]+$/.test(url)) return url;
  return null;
}

/**
 * Convert paint condition slider value to descriptive text
 * @param {number|string} value - Slider value (0-8)
 * @returns {string} - Descriptive text
 */
function getPaintConditionLabel(value) {
  const numValue = parseInt(value);
  const labels = [
    'Not Inspected',   // 0
    'Excellent',       // 1
    'Exc-Good',        // 2
    'Good',            // 3
    'Good-Fair',       // 4
    'Fair',            // 5
    'Fair-Poor',       // 6
    'Poor',            // 7
    'Very Poor'        // 8
  ];
  return labels[numValue] || 'Not assessed';
}

/**
 * Convert growth level slider value to descriptive text
 * @param {number|string} value - Slider value (0-8)
 * @returns {string} - Descriptive text
 */
function getGrowthLevelLabel(value) {
  const numValue = parseInt(value);
  const labels = [
    'Not Inspected',      // 0
    'Minimal',            // 1
    'Minimal-Moderate',   // 2
    'Moderate',           // 3
    'Moderate-Heavy',     // 4
    'Heavy',              // 5
    'Heavy-Severe',       // 6
    'Severe',             // 7
    'Extreme'             // 8
  ];
  return labels[numValue] || 'Not assessed';
}

/**
 * Capitalize first letter of string
 * @param {string} str - String to capitalize
 * @returns {string} - Capitalized string
 */
function capitalizeFirst(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert thru-hull condition value to human-readable label
 * @param {string} value - Raw database value (e.g., 'needs_attention')
 * @returns {string} - Human-readable label (e.g., 'Needs Attention')
 */
function getThruHullLabel(value) {
  if (!value) return 'Not assessed';
  const labels = {
    'excellent': 'Excellent',
    'good': 'Good',
    'fair': 'Fair',
    'sound': 'Sound',
    'needs_attention': 'Needs Attention',
    'critical': 'Critical',
    'clear': 'Clear',
    'partially_blocked': 'Partially Blocked'
  };
  return labels[value] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function initEnhancedChargeFlow() {
  window.addEventListener('DOMContentLoaded', function() {
      setTimeout(() => {
          // Create adminApp if it doesn't exist
          if (!window.adminApp) {
              window.adminApp = {};
          }

          // Assign the charge implementation directly
          window.adminApp.chargeCustomer = async function() {
                  console.log('🚀 Enhanced chargeCustomer called');

                  const customer = window.selectedCustomer || window.adminApp.selectedCustomer;
                  const paymentMethodId = window.selectedPaymentMethodId || window.customerPaymentMethodId;

                  console.log('Customer:', customer);
                  console.log('Payment Method ID:', paymentMethodId);
                  console.log('Service Key:', window.currentServiceKey);

                  // If we have a customer with saved payment method, charge directly
                  if (customer && paymentMethodId && window.currentServiceKey) {
                      console.log('✅ Using saved payment method - charging directly');

                      try {
                          // Get the amount - check multiple sources for the final price
                          let amount = 0;

                          // First try wizardTotalPrice (used by wizard forms with adjustments)
                          const wizardTotalPrice = document.getElementById('wizardTotalPrice');
                          if (wizardTotalPrice && wizardTotalPrice.textContent) {
                              amount = parseFloat(wizardTotalPrice.textContent.replace('$', '').replace(',', ''));
                              console.log('Using wizardTotalPrice:', amount);
                          }

                          // Fall back to totalCostDisplay if wizardTotalPrice not found
                          if (!amount || amount === 0) {
                              const totalCostDisplay = document.getElementById('totalCostDisplay');
                              if (totalCostDisplay) {
                                  const displayValue = totalCostDisplay.value || totalCostDisplay.textContent || '0';
                                  amount = parseFloat(displayValue.replace('$', '').replace(',', ''));
                                  console.log('Using totalCostDisplay:', amount);
                              }
                          }

                          // Check if adminApp has the final price (with adjustments applied)
                          if (window.adminApp && window.adminApp.finalPrice && window.adminApp.finalPrice > 0) {
                              amount = window.adminApp.finalPrice;
                              console.log('Using adminApp.finalPrice (adjusted):', amount);
                          }

                          if (amount <= 0) {
                              alert('Invalid charge amount');
                              return;
                          }

                          console.log(`💰 Charging $${amount} to ${customer.name} using ${paymentMethodId}`);

                          // ===== INVENTORY REVIEW MODAL INTEGRATION =====
                          // Check if there are anode assignments that need inventory processing
                          const anodeAssignments = Object.values(window.anodeAssignments || {}).filter(a =>
                              a.anode_id && (a.status === 'replaced' || a.status === 'retrieve' || a.status === 'order')
                          );

                          if (anodeAssignments.length > 0) {
                              console.log(`📦 Found ${anodeAssignments.length} anodes requiring inventory actions`);

                              // Show inventory review modal and wait for confirmation
                              try {
                                  await new Promise((resolve, reject) => {
                                      openInventoryReviewModal(anodeAssignments, async () => {
                                          console.log('✅ Inventory review confirmed, processing inventory actions...');

                                          // Get boat and service log IDs
                                          const boatId = window.selectedBoat?.id || null;
                                          const serviceLogId = window.currentServiceLogId || null; // TODO: May need to create service log first

                                          if (!boatId) {
                                              console.warn('⚠️ Boat ID not found - skipping inventory processing');
                                              resolve(); // Continue with payment anyway
                                              return;
                                          }

                                          // Prepare anodes payload with inventory IDs
                                          // inventory_id is attached by inventory-review-modal.js after fetching from anode_inventory table
                                          const anodesPayload = anodeAssignments.map(anode => ({
                                              status: anode.status,
                                              anode_id: anode.anode_id,
                                              anode_inventory_id: anode.inventory_id,
                                              anode_name: anode.anode_name
                                          }));

                                          try {
                                              const { data: inventoryResult, error: inventoryError } = await window.supabaseClient.functions.invoke('finalize-service-inventory', {
                                                  body: {
                                                      serviceLogId: serviceLogId || 'temp-' + Date.now(), // Temporary ID if not created yet
                                                      boatId: boatId,
                                                      anodes: anodesPayload
                                                  }
                                              });

                                              if (inventoryError) {
                                                  console.error('❌ Error calling finalize-service-inventory API:', inventoryError);
                                                  console.warn('⚠️ Inventory processing failed - continuing with charge anyway');
                                                  resolve(); // Continue with payment anyway
                                                  return;
                                              }

                                              if (inventoryResult && inventoryResult.success) {
                                                  console.log('✅ Inventory actions processed:', inventoryResult.summary);
                                                  resolve(); // Continue with payment
                                              } else {
                                                  console.error('❌ Inventory processing failed:', inventoryResult);
                                                  if (inventoryResult?.results?.errors) {
                                                      console.error('   Inventory errors:', inventoryResult.results.errors);
                                                  }
                                                  console.warn('⚠️ Inventory processing failed - continuing with charge anyway');
                                                  resolve(); // Continue with payment anyway (non-blocking)
                                              }
                                          } catch (err) {
                                              console.error('❌ Error calling finalize-service-inventory API:', err);
                                              console.warn('⚠️ Inventory processing failed - continuing with charge anyway');
                                              resolve(); // Continue with payment anyway
                                          }
                                      });
                                  });
                              } catch (error) {
                                  console.error('Inventory review cancelled or failed:', error);
                                  console.warn('⚠️ Continuing with charge despite inventory errors');
                                  // Continue with charge flow even if inventory review fails
                              }
                          }
                          // ===== END INVENTORY REVIEW MODAL INTEGRATION =====

                          // Show loading state
                          const chargeButton = document.getElementById('chargeButton');
                          const originalButtonText = chargeButton.textContent;
                          chargeButton.disabled = true;
                          chargeButton.textContent = '💳 Processing...';

                          // Collect metadata
                          // Get proper service display name
                          let serviceName = window.currentServiceKey.replace(/_/g, ' ');
                          if (window.serviceData && window.serviceData[window.currentServiceKey]) {
                              serviceName = window.serviceData[window.currentServiceKey].name;
                              console.log('Using service display name:', serviceName);
                          }

                          // Get boat details from multiple sources
                          let boatName = window.selectedBoat?.name ||
                                        document.getElementById('wizardBoatName')?.value ||
                                        document.getElementById('boatName')?.value || '';

                          let boatLength = window.selectedBoat?.length ||
                                          document.getElementById('wizardBoatLength')?.value ||
                                          document.getElementById('boatLength')?.value || '';

                          console.log('Boat details:', { boatName, boatLength });

                          const metadata = {
                              service_key: window.currentServiceKey,
                              service_name: serviceName,
                              customer_name: customer.name,
                              customer_email: customer.email,
                              boat_name: boatName,
                              boat_length: boatLength
                          };

                          const description = `${metadata.service_name} - ${customer.name}${metadata.boat_name ? ' - ' + metadata.boat_name : ''}`;

                          // Call Supabase Edge Function to charge customer
                          const { data: result, error: chargeError } = await window.supabaseClient.functions.invoke('charge-customer', {
                              body: {
                                  customerId: customer.id,
                                  amount: Math.round(amount * 100), // Convert to cents
                                  description: description,
                                  metadata: metadata,
                                  paymentMethodId: paymentMethodId
                              }
                          });

                          if (chargeError) {
                              console.error('❌ Error calling charge-customer function:', chargeError);
                              throw new Error(chargeError.message || 'Failed to charge customer');
                          }

                          if (result.success) {
                              console.log('✅ Charge successful:', result);

                              // KEEP button disabled permanently to prevent double-charging
                              chargeButton.disabled = true;
                              chargeButton.textContent = '✅ Payment Completed';
                              chargeButton.style.background = '#4caf50';
                              chargeButton.style.cursor = 'not-allowed';

                              // ===== BUILD CHARGE BREAKDOWN FIRST =====
                              // Build this BEFORE invoice creation so line items are included
                              const chargeBreakdown = {
                                  lineItems: [],
                                  subtotal: 0,
                                  discounts: [],
                                  total: amount,
                                  serviceType: window.currentServiceKey,
                                  // Add boat configuration details
                                  boatDetails: {
                                      name: boatName,
                                      length: boatLength,
                                      type: document.querySelector('input[name="wizard_boat_type"]:checked')?.value ||
                                            document.querySelector('input[name="boat_type"]:checked')?.value || '',
                                      hullType: document.querySelector('input[name="wizard_hull_type"]:checked')?.value ||
                                               document.querySelector('input[name="hull_type"]:checked')?.value || '',
                                      twinEngines: document.getElementById('wizard_twin_engines')?.checked ||
                                                  document.getElementById('has_twin_engines')?.checked || false,
                                      paintCondition: document.getElementById('wizardPaintCondition')?.value ||
                                                    document.getElementById('actualPaintCondition')?.value || '',
                                      growthLevel: document.getElementById('wizardGrowthLevel')?.value ||
                                                  document.getElementById('actualGrowthLevel')?.value || '',
                                      propellerCount: parseInt(document.getElementById('wizardPropellerCount')?.value || window.selectedPropellerCount || window.selectedBoat?.propeller_count || 0)
                                  },
                                  // Add wizard notes if available
                                  notes: {
                                      thruHull: document.getElementById('wizardThruHull')?.value || '',
                                      thruHullNotes: document.getElementById('wizardThruHullNotes')?.value || '',
                                      propeller1: document.getElementById('wizardPropeller1')?.value || '',
                                      propeller2: document.getElementById('wizardPropeller2')?.value || '',
                                      propellerNotes: document.getElementById('wizardPropellerNotes')?.value || '',
                                      generalNotes: document.getElementById('wizardGeneralNotes')?.value || ''
                                  },
                                  // Add surcharges breakdown
                                  surcharges: [],
                                  // Add detailed anode assignments for report-style display
                                  anodeDetails: []
                              };

                              // Get base service price from multiple sources
                              let basePrice = 0;

                              // Check minimum fee checkbox state for anodes_only service
                              const chargeMinFeeCheckbox = document.getElementById('chargeMinimumFee');
                              const shouldChargeMinFee = !chargeMinFeeCheckbox || chargeMinFeeCheckbox.checked;

                              // For anodes_only service with unchecked minimum fee, skip base price entirely
                              if (window.currentServiceKey === 'anodes_only' && !shouldChargeMinFee) {
                                  basePrice = 0;
                                  console.log('Anodes only service with no minimum fee - skipping base price');
                              } else {
                                  // Try to get the calculated base price (before adjustments)
                                  if (window.wizardBasePrice && window.wizardBasePrice > 0) {
                                      basePrice = window.wizardBasePrice;
                                      console.log('Using wizardBasePrice for breakdown:', basePrice);
                                  } else if (document.getElementById('servicePrice')?.value) {
                                      basePrice = parseFloat(document.getElementById('servicePrice').value);
                                      console.log('Using servicePrice input for breakdown:', basePrice);
                                  }

                                  // If we still don't have a base price, calculate it from the service and boat
                                  if (basePrice === 0 && window.serviceData && window.currentServiceKey) {
                                      const service = window.serviceData[window.currentServiceKey];
                                      const boatLength = parseFloat(document.getElementById('boatLength')?.value || document.getElementById('wizardBoatLength')?.value || 0);

                                      if (service.type === 'per_foot' && boatLength > 0) {
                                          // Calculate base price with all surcharges (before any discounts)
                                          basePrice = boatLength * service.rate;
                                          console.log(`Calculated base from service: ${boatLength}ft × $${service.rate}/ft = $${basePrice}`);

                                          // Note: This is the raw base before surcharges, but it's better than nothing
                                          // Ideally we'd have wizardBasePrice set during calculation
                                      } else if (service.type === 'flat') {
                                          basePrice = service.rate;
                                          console.log('Using flat service rate:', basePrice);
                                      }
                                  }
                              }

                              // Add base service as line item if we have it
                              if (basePrice > 0) {
                                  chargeBreakdown.lineItems.push({
                                      name: metadata.service_name,
                                      quantity: 1,
                                      price: basePrice,
                                      total: basePrice
                                  });
                                  chargeBreakdown.subtotal += basePrice;
                                  console.log('Added base service to line items:', basePrice);
                              }

                              // Add anodes from cart if any
                              if (window.anodeCart && Object.keys(window.anodeCart).length > 0) {
                                  Object.values(window.anodeCart).forEach(anode => {
                                      const anodeTotal = (anode.selling_price || anode.list_price || 0) * (anode.quantity || 1);
                                      const anodeLocation = anode.location ? ` (${anode.location}${anode.position ? ' - ' + anode.position : ''})` : '';
                                      chargeBreakdown.lineItems.push({
                                          name: anode.name + anodeLocation,
                                          quantity: anode.quantity || 1,
                                          price: anode.selling_price || anode.list_price || 0,
                                          total: anodeTotal
                                      });
                                      chargeBreakdown.subtotal += anodeTotal;
                                  });
                              }

                              // Add anodes from assignments if any (for anodes_only service)
                              if (window.currentServiceKey === 'anodes_only' && window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                                  const assignedAnodes = Object.values(window.anodeAssignments).filter(a =>
                                      a.anode_id && (a.status === 'replaced' || a.status === 'retrieve' || a.status === 'order')
                                  );

                                  if (assignedAnodes.length > 0 && Object.keys(window.anodeCart || {}).length === 0) {
                                      // Only add if cart is empty (to avoid duplicates)
                                      assignedAnodes.forEach(anode => {
                                          const anodeLocation = anode.location ? ` (${anode.location}${anode.position ? ' - ' + anode.position : ''})` : '';
                                          const anodePrice = anode.anode_price || 0;
                                          chargeBreakdown.lineItems.push({
                                              name: (anode.anode_name || 'Anode') + anodeLocation,
                                              quantity: 1,
                                              price: anodePrice,
                                              total: anodePrice
                                          });
                                          chargeBreakdown.subtotal += anodePrice;
                                      });
                                  }
                              }

                              // Collect ALL anode details for report-style display (including inspected)
                              if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                                  const laborPerAnode = 15; // $15 labor per anode
                                  Object.entries(window.anodeAssignments).forEach(([key, anode]) => {
                                      chargeBreakdown.anodeDetails.push({
                                          location: anode.location || 'unknown',
                                          position: anode.position || 'unknown',
                                          condition: anode.condition !== undefined && anode.condition !== null ? anode.condition : 100,
                                          status: anode.status || 'inspected',
                                          name: anode.anode_name || 'Not Selected',
                                          price: parseFloat(anode.anode_price) || 0,
                                          labor: anode.status === 'replaced' ? laborPerAnode : 0
                                      });
                                  });
                              }

                              // Calculate discount if total is less than subtotal
                              if (chargeBreakdown.subtotal > amount) {
                                  const discountAmount = chargeBreakdown.subtotal - amount;

                                  // Try to get a descriptive discount name
                                  let discountName = 'Discount';
                                  if (window.adminApp?.priceAdjustment) {
                                      const adj = window.adminApp.priceAdjustment;
                                      if (adj.type === 'percent') {
                                          discountName = `Discount (${adj.value}%)`;
                                      } else if (adj.type === 'dollar') {
                                          discountName = `Discount ($${adj.value})`;
                                      } else if (adj.type === 'custom') {
                                          discountName = 'Adjustment';
                                      }
                                  }

                                  chargeBreakdown.discounts.push({
                                      name: discountName,
                                      amount: discountAmount
                                  });
                                  console.log('Added discount to breakdown:', discountName, discountAmount);
                              } else if (chargeBreakdown.subtotal === 0 && basePrice === 0 && amount > 0) {
                                  // If we couldn't get the base price, create a simple line item with the charged amount
                                  chargeBreakdown.lineItems = [{
                                      name: metadata.service_name,
                                      quantity: 1,
                                      price: amount,
                                      total: amount
                                  }];
                                  chargeBreakdown.subtotal = amount;
                                  console.log('Created simple line item with charged amount:', amount);
                              }

                              // ===== CREATE INVOICE WITH FULL LINE ITEM DETAILS =====
                              // Invoice created AFTER chargeBreakdown so all details are included
                              try {
                                  console.log('📄 Creating invoice with full line item details...');

                                  const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                                      ? 'http://localhost:3001/api'
                                      : '/api';

                                  // Prepare customer and boat details for invoice
                                  const customerDetails = {
                                      name: customer.name,
                                      email: customer.email,
                                      phone: customer.phone || ''
                                  };

                                  const invoiceBoatDetails = boatName ? {
                                      name: boatName,
                                      length: boatLength,
                                      type: chargeBreakdown.boatDetails?.type || '',
                                      hullType: chargeBreakdown.boatDetails?.hullType || '',
                                      paintCondition: chargeBreakdown.boatDetails?.paintCondition || '',
                                      growthLevel: chargeBreakdown.boatDetails?.growthLevel || ''
                                  } : {};

                                  const invoiceResponse = await fetch(`${API_BASE_URL}/invoices`, {
                                      method: 'POST',
                                      headers: {
                                          'Content-Type': 'application/json'
                                      },
                                      body: JSON.stringify({
                                          customerId: customer.id,
                                          serviceId: window.selectedService?.id || null,
                                          boatId: window.selectedBoat?.id || null,
                                          amount: amount,
                                          status: 'paid',
                                          paymentMethod: 'stripe',
                                          paymentReference: result.paymentIntent.id,
                                          paidAt: new Date().toISOString(),
                                          customerDetails: customerDetails,
                                          boatDetails: invoiceBoatDetails,
                                          serviceDetails: {
                                              service_name: metadata.service_name,
                                              service_key: window.currentServiceKey,
                                              line_items: chargeBreakdown.lineItems,
                                              surcharges: chargeBreakdown.surcharges || [],
                                              anode_details: chargeBreakdown.anodeDetails || [],
                                              notes: chargeBreakdown.notes || {},
                                              subtotal: chargeBreakdown.subtotal,
                                              discounts: chargeBreakdown.discounts,
                                              total: amount
                                          },
                                          notes: document.getElementById('wizardGeneralNotes')?.value || '',
                                          dueInDays: 0
                                      })
                                  });

                                  const invoiceResult = await invoiceResponse.json();
                                  if (invoiceResult.success) {
                                      console.log('✅ Invoice created with full details:', invoiceResult.invoice.invoice_number);
                                      window.lastChargeInvoiceId = invoiceResult.invoice.id;
                                      window.lastChargeInvoiceNumber = invoiceResult.invoice.invoice_number;
                                  } else {
                                      console.error('⚠️ Failed to create invoice:', invoiceResult.error);
                                  }
                              } catch (invoiceError) {
                                  console.error('⚠️ Error creating invoice (non-critical):', invoiceError);
                              }
                              // ===== END INVOICE CREATION =====

                              // Show UNMISSABLE success modal
                              const successModal = document.getElementById('successModal');
                              const successContent = document.getElementById('successModalContent');

                              // Build charge summary for modal
                              let chargeSummaryHTML = '';
                              if (chargeBreakdown.lineItems && chargeBreakdown.lineItems.length > 0) {
                                  chargeSummaryHTML = '<div style="margin-top: 15px; border-top: 2px solid #e0e0e0; padding-top: 15px;">';

                                  // Show line items
                                  chargeBreakdown.lineItems.forEach(item => {
                                      chargeSummaryHTML += `
                                          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px;">
                                              <span>${item.name} ${item.quantity > 1 ? `(×${item.quantity})` : ''}</span>
                                              <span>$${item.total.toFixed(2)}</span>
                                          </div>
                                      `;
                                  });

                                  // Show subtotal if there are discounts
                                  if (chargeBreakdown.discounts && chargeBreakdown.discounts.length > 0) {
                                      chargeSummaryHTML += `
                                          <div style="display: flex; justify-content: space-between; margin: 8px 0; padding-top: 8px; border-top: 1px solid #e0e0e0; font-size: 14px;">
                                              <span style="color: #666;">Subtotal:</span>
                                              <span style="font-weight: 600;">$${chargeBreakdown.subtotal.toFixed(2)}</span>
                                          </div>
                                      `;

                                      // Show discounts
                                      chargeBreakdown.discounts.forEach(discount => {
                                          chargeSummaryHTML += `
                                              <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; color: #16a34a;">
                                                  <span>${discount.name}:</span>
                                                  <span style="font-weight: 600;">-$${discount.amount.toFixed(2)}</span>
                                              </div>
                                          `;
                                      });
                                  }

                                  chargeSummaryHTML += '</div>';
                              }

                              successContent.innerHTML = `
                                  <div style="position: relative; text-align: center; padding: 30px;">
                                      <button onclick="document.getElementById('successModal').style.display='none'" style="position: absolute; top: 10px; right: 10px; background: transparent; border: none; font-size: 32px; cursor: pointer; color: #666; padding: 5px 15px; line-height: 1;" title="Close and review charge details">
                                          ×
                                      </button>

                                      <div style="font-size: 72px; margin-bottom: 20px;">✅</div>
                                      <h2 style="color: #2e7d32; margin: 0 0 20px 0;">Payment Successful!</h2>

                                      <div style="background: white; padding: 25px; border-radius: 8px; margin: 20px 0; text-align: left;">
                                          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
                                              <div>
                                                  <div style="color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Customer</div>
                                                  <div style="font-weight: 600;">${customer.name}</div>
                                                  <div style="color: #888; font-size: 14px;">${customer.email}</div>
                                              </div>
                                              <div style="text-align: right;">
                                                  <div style="color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Amount Charged</div>
                                                  <div style="font-size: 32px; font-weight: 700; color: #2e7d32;">$${amount.toFixed(2)}</div>
                                              </div>
                                          </div>

                                          ${chargeSummaryHTML}

                                          <div style="border-top: 1px solid #e0e0e0; padding-top: 15px; font-size: 14px; color: #666; margin-top: 15px;">
                                              <div><strong>Service:</strong> ${metadata.service_name}</div>
                                              ${metadata.boat_name ? `<div style="margin-top: 5px;"><strong>Boat:</strong> ${metadata.boat_name}</div>` : ''}
                                              <div style="margin-top: 5px;"><strong>Payment ID:</strong> ${result.paymentIntent.id}</div>
                                              <div style="margin-top: 5px;"><strong>Charge ID:</strong> ${result.chargeId}</div>
                                          </div>
                                      </div>

                                      <div style="padding: 15px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 6px; margin: 20px 0; font-size: 14px; color: #856404;">
                                          📧 Email receipt will be sent to ${customer.email}
                                      </div>

                                      <div style="display: flex; gap: 15px; justify-content: center; flex-wrap: wrap;">
                                          <button onclick="document.getElementById('successModal').style.display='none'" style="padding: 15px 40px; background: #4caf50; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 16px; font-weight: 600; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
                                              ✓ Review Charge Details
                                          </button>
                                          <button onclick="window.location.reload()" style="padding: 15px 40px; background: #2196F3; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 16px; font-weight: 600; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
                                              ← Start New Charge
                                          </button>
                                      </div>
                                  </div>
                              `;

                              successModal.style.display = 'block';

                              // Store for conditions logging
                              window.lastChargePaymentIntentId = result.paymentIntent.id;
                              window.lastChargeDetails = {
                                  amount: amount,
                                  customer: customer,
                                  metadata: metadata,
                                  paymentIntentId: result.paymentIntent.id,
                                  chargeId: result.chargeId
                              };

                              // Send email receipt to customer via Supabase Edge Function
                              (async () => {
                                  try {
                                      console.log('📧 Sending receipt email to customer via Supabase...');

                                      // Save boat conditions to service_logs table (single entry with all data)
                                      console.log('💾 Saving boat conditions and service log...');
                                      let serviceLogId = null;
                                      try {
                                          const conditionsData = {
                                              customer_id: customer.id,
                                              boat_id: window.selectedBoat?.id || null,
                                              service_id: null,  // No prior entry, create new
                                              order_id: result.paymentIntent.id,
                                              service_type: window.currentServiceKey,
                                              service_name: metadata.service_name,
                                              service_date: window.serviceDate || getTodayLocal(),
                                              time_in: document.getElementById('wizardStartTime')?.value || window.startTime || null,
                                              time_out: document.getElementById('wizardEndTime')?.value || window.endTime || null,
                                              service_time: document.getElementById('wizardStartTime')?.value || window.startTime || new Date().toTimeString().split(' ')[0],
                                              // Read from slider data-value first (has text labels like "good", "moderate")
                                              // Fallback to other sources only if slider not found
                                              paint_condition_overall: document.getElementById('actualPaintCondition')?.getAttribute('data-value') ||
                                                                     document.getElementById('wizardPaintCondition')?.value ||
                                                                     chargeBreakdown.boatDetails?.paintCondition || null,
                                              growth_level: document.getElementById('actualGrowthLevel')?.getAttribute('data-value') ||
                                                          document.getElementById('wizardGrowthLevel')?.value ||
                                                          chargeBreakdown.boatDetails?.growthLevel || null,
                                              thru_hull_condition: chargeBreakdown.notes?.thruHull ||
                                                                 document.getElementById('wizardThruHull')?.value || null,
                                              thru_hull_notes: chargeBreakdown.notes?.thruHullNotes ||
                                                             document.getElementById('wizardThruHullNotes')?.value || null,
                                              // Collect up to 5 propellers
                                              propellers: (() => {
                                                  const props = [];
                                                  for (let i = 1; i <= 5; i++) {
                                                      const condition = document.getElementById(`wizardPropeller${i}`)?.value ||
                                                                       (i === 1 ? chargeBreakdown.notes?.propeller1 : null) ||
                                                                       (i === 2 ? chargeBreakdown.notes?.propeller2 : null);
                                                      const notes = document.getElementById(`wizardPropeller${i}Notes`)?.value || '';

                                                      if (condition && condition !== '' && condition !== 'not_inspected') {
                                                          props.push({
                                                              number: i,
                                                              condition: condition,
                                                              notes: notes
                                                          });
                                                      }
                                                  }
                                                  return props;
                                              })(),
                                              propeller_notes: chargeBreakdown.notes?.propellerNotes ||
                                                             document.getElementById('wizardPropellerNotes')?.value || null,
                                              notes: chargeBreakdown.notes?.generalNotes ||
                                                   document.getElementById('wizardGeneralNotes')?.value || null,
                                              // Collect anode conditions from assignments and cart
                                              anode_conditions: (() => {
                                                  const conditions = [];
                                                  // From anodeAssignments (location-based tracking)
                                                  if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                                                      Object.values(window.anodeAssignments).forEach(anode => {
                                                          conditions.push({
                                                              location: anode.location,
                                                              position: anode.position,
                                                              condition_percent: anode.condition,
                                                              checked_date: new Date().toISOString()
                                                          });
                                                      });
                                                  }
                                                  // From anodeCart (catalog-based selection)
                                                  if (window.anodeCart && Object.keys(window.anodeCart).length > 0) {
                                                      Object.values(window.anodeCart).forEach(anode => {
                                                          conditions.push({
                                                              anode_id: anode.id,
                                                              anode_name: anode.name,
                                                              location: anode.location,
                                                              position: anode.position,
                                                              condition_percent: anode.condition_percent || null,
                                                              notes: anode.notes || null
                                                          });
                                                      });
                                                  }
                                                  return conditions;
                                              })(),
                                              // Track newly installed anodes from both systems
                                              anodes_installed: (() => {
                                                  const installed = [];
                                                  // From anodeAssignments (all replacement types)
                                                  if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                                                      Object.values(window.anodeAssignments).forEach(anode => {
                                                          // Track all replacement types (replaced, retrieve, order)
                                                          // All represent anodes that need to be installed/replaced
                                                          if (anode.status === 'replaced' || anode.status === 'retrieve' || anode.status === 'order') {
                                                              // Generate a descriptive fallback name if none exists
                                                              const location = anode.location || 'unknown';
                                                              const position = anode.position || 'unknown';
                                                              const fallbackName = `Anode (${location} - ${position})`;
                                                              const anodeName = anode.anode_name || fallbackName;

                                                              installed.push({
                                                                  anode_id: anode.anode_id || null,
                                                                  anode_name: anodeName,  // Guaranteed to have a value
                                                                  catalog_id: anode.anode_id || null,  // catalog_id same as anode_id (from boat data)
                                                                  location: anode.location,
                                                                  position: anode.position,
                                                                  quantity: 1,
                                                                  condition_before: anode.condition,
                                                                  price: anode.anode_price || null,  // Use anode_price from boat data
                                                                  status: anode.status,  // IMPORTANT: Preserves status for downstream actions
                                                                  replaced_on_service: true
                                                              });
                                                          }
                                                      });
                                                  }
                                                  // From anodeCart (catalog selections)
                                                  if (window.anodeCart && Object.keys(window.anodeCart).length > 0) {
                                                      Object.values(window.anodeCart).forEach(anode => {
                                                          if (anode.quantity > 0) {
                                                              // Ensure we have a name, with fallback
                                                              const anodeName = anode.name || anode.anode_name || 'Anode (catalog item)';

                                                              installed.push({
                                                                  anode_id: anode.id,
                                                                  anode_name: anodeName,  // Guaranteed to have a value
                                                                  catalog_id: anode.catalog_id,
                                                                  quantity: anode.quantity,
                                                                  location: anode.location,
                                                                  position: anode.position,
                                                                  price: anode.selling_price || anode.list_price
                                                              });
                                                          }
                                                      });
                                                  }
                                                  return installed;
                                              })(),
                                              created_by: 'billing_system',
                                              invoice_id: window.lastChargeInvoiceId || null,  // Link to invoice
                                              email_sent: true  // Prevent trigger from sending duplicate email
                                          };

                                          const { data: conditionsResult, error: conditionsError } = await window.supabaseClient.functions.invoke('save-conditions', {
                                              body: conditionsData
                                          });

                                          if (conditionsError) {
                                              console.error('⚠️ Failed to save service log:', conditionsError);
                                          } else if (conditionsResult && conditionsResult.success) {
                                              serviceLogId = conditionsResult.service_log_id;
                                              console.log('✅ Service log saved:', serviceLogId);
                                          } else {
                                              console.error('⚠️ Failed to save service log:', conditionsResult?.error);
                                          }
                                      } catch (conditionsError) {
                                          console.error('⚠️ Error saving boat conditions (non-critical):', conditionsError);
                                      }

                                      // Save boat anodes for persistence across future services
                                      if (window.selectedBoat?.id && window.anodeAssignments) {
                                          console.log('💾 Saving boat anodes for future persistence...');
                                          try {
                                              await window.saveBoatAnodes(window.selectedBoat.id, window.anodeAssignments);
                                              console.log('✅ Boat anodes saved successfully');
                                          } catch (anodeError) {
                                              console.error('⚠️ Error saving boat anodes (non-critical):', anodeError);
                                          }
                                      } else {
                                          console.log('ℹ️ No boat or anode assignments to save');
                                      }

                                      // Use the chargeBreakdown already calculated above (before modal)
                                      console.log('Using pre-calculated chargeBreakdown for email:', chargeBreakdown);

                                      // Build boat conditions section (Paint, Growth, Thru-Hull)
                                      const paintCondition = document.getElementById('actualPaintCondition')?.getAttribute('data-value') ||
                                                           document.getElementById('wizardPaintCondition')?.value || '';
                                      const growthLevel = document.getElementById('actualGrowthLevel')?.getAttribute('data-value') ||
                                                        document.getElementById('wizardGrowthLevel')?.value || '';
                                      const thruHullCondition = document.getElementById('wizardThruHull')?.value || '';

                                      const boatConditionsSection = (paintCondition || growthLevel || thruHullCondition)
                                        ? `<tr>
                                            <td style="padding: 0 40px 30px;">
                                              <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px; font-weight: 600;">Boat Conditions</h3>
                                              <table width="100%" cellpadding="8" cellspacing="0">
                                                ${paintCondition ? `<tr>
                                                  <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                                                    <p style="margin: 0; color: #6b7280; font-size: 14px;">Paint Condition</p>
                                                  </td>
                                                  <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                                                    <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(paintCondition)}</p>
                                                  </td>
                                                </tr>` : ''}
                                                ${growthLevel ? `<tr>
                                                  <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                                                    <p style="margin: 0; color: #6b7280; font-size: 14px;">Growth Level</p>
                                                  </td>
                                                  <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                                                    <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(growthLevel)}</p>
                                                  </td>
                                                </tr>` : ''}
                                                ${thruHullCondition ? `<tr>
                                                  <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                                                    <p style="margin: 0; color: #6b7280; font-size: 14px;">Thru-Hull Condition</p>
                                                  </td>
                                                  <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                                                    <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(thruHullCondition.replace(/_/g, ' '))}</p>
                                                  </td>
                                                </tr>` : ''}
                                              </table>
                                            </td>
                                          </tr>`
                                        : '';

                                      // Build anode details HTML section (conditional)
                                      const anodeDetailsSection = chargeBreakdown.anodeDetails?.length > 0
                                        ? `<tr>
                                            <td style="padding: 0 40px 30px;">
                                              <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px;">Anode Inspection & Replacements</h3>
                                              <table width="100%" cellpadding="8" cellspacing="0" style="border: 1px solid #e5e7eb;">
                                                <thead>
                                                  <tr style="background-color: #f9fafb;">
                                                    <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Location</th>
                                                    <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Condition</th>
                                                    <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Status</th>
                                                    <th style="padding: 12px; text-align: right; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Cost</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  ${chargeBreakdown.anodeDetails.map(anode => `
                                                    <tr>
                                                      <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${anode.location} - ${anode.position}</td>
                                                      <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${anode.condition}%</td>
                                                      <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${capitalizeFirst(anode.status)}</td>
                                                      <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; color: #111827; font-size: 14px; font-weight: 600;">${anode.status === 'replaced' ? '$' + (anode.price + anode.labor).toFixed(2) : 'N/A'}</td>
                                                    </tr>
                                                  `).join('')}
                                                </tbody>
                                              </table>
                                            </td>
                                          </tr>`
                                        : '';

                                      // Build propeller inspection HTML section (conditional)
                                      const propellerSection = (chargeBreakdown.notes?.propeller1 || chargeBreakdown.notes?.propeller2)
                                        ? `<tr>
                                            <td style="padding: 0 40px 30px;">
                                              <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px;">Propeller Inspection</h3>
                                              <div style="background-color: #f9fafb; padding: 16px; border-radius: 6px;">
                                                ${chargeBreakdown.notes.propeller1 ? `<p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Propeller 1:</strong> ${capitalizeFirst(chargeBreakdown.notes.propeller1)}</p>` : ''}
                                                ${chargeBreakdown.notes.propeller2 ? `<p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Propeller 2:</strong> ${capitalizeFirst(chargeBreakdown.notes.propeller2)}</p>` : ''}
                                                ${chargeBreakdown.notes.propellerNotes ? `<p style="margin: 8px 0 0; padding-top: 8px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;"><strong>Notes:</strong> ${chargeBreakdown.notes.propellerNotes}</p>` : ''}
                                              </div>
                                            </td>
                                          </tr>`
                                        : '';

                                      // Fetch and build YouTube videos section
                                      let videosSection = '';
                                      try {
                                        const boatId = window.selectedBoat?.id;
                                        if (boatId) {
                                          const { data: playlist } = await window.supabaseClient
                                            .from('youtube_playlists')
                                            .select('playlist_id, playlist_url')
                                            .eq('boat_id', boatId)
                                            .eq('is_public', true)
                                            .maybeSingle();

                                          if (playlist) {
                                            const playlistId = playlist.playlist_id || extractPlaylistId(playlist.playlist_url);

                                            if (playlistId) {
                                              const { data: videosData } = await window.supabaseClient.functions.invoke('get-playlist-videos', {
                                                body: {
                                                  playlistId,
                                                  serviceDate: new Date().toISOString(),
                                                  maxResults: 4
                                                }
                                              });

                                              if (videosData?.videos?.length > 0) {
                                                const videoThumbnails = videosData.videos.map(video => `
                                                  <td style="width: 50%; padding: 6px;">
                                                    <a href="${video.url}" style="display: block; text-decoration: none;">
                                                      <img src="${video.thumbnail}" alt="${video.title}" style="width: 100%; border-radius: 4px; border: 1px solid #e5e7eb; display: block;" />
                                                      <p style="margin: 8px 0 0; font-size: 12px; color: #6b7280; text-align: center;">${video.title}</p>
                                                    </a>
                                                  </td>
                                                `).join('');

                                                videosSection = `<tr>
                                                  <td style="padding: 0 40px 30px;">
                                                    <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px;">Service Videos</h3>
                                                    <div style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; margin-bottom: 16px;">
                                                      <p style="margin: 0; color: #1e3a8a; font-size: 14px; line-height: 1.5;">
                                                        <strong>Note:</strong> Videos may still be uploading. If you don't see them yet, please check back later or contact us at info@sailorskills.com.
                                                      </p>
                                                    </div>
                                                    <table width="100%" cellpadding="6" cellspacing="0">
                                                      <tr>${videoThumbnails}</tr>
                                                    </table>
                                                  </td>
                                                </tr>`;
                                              }
                                            }
                                          }
                                        }
                                      } catch (error) {
                                        console.warn('Failed to fetch YouTube videos for email:', error);
                                      }

                                      // Build pricing breakdown HTML (email-safe table layout)
                                      const pricingBreakdownHtml = `<tr>
                                        <td style="padding: 0 40px 30px;">
                                          <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px; font-weight: 600;">Service Summary</h3>
                                          <table width="100%" cellpadding="0" cellspacing="0" style="border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden;">
                                            ${chargeBreakdown.lineItems.map(item =>
                                              `<tr style="border-bottom: 1px solid #e5e7eb;">
                                                <td style="padding: 12px 16px; color: #374151; font-size: 14px;">${item.name}</td>
                                                <td style="padding: 12px 16px; text-align: right; color: #374151; font-size: 14px;">$${item.total.toFixed(2)}</td>
                                              </tr>`
                                            ).join('')}
                                            ${chargeBreakdown.discounts?.length > 0
                                              ? chargeBreakdown.discounts.map(d =>
                                                  `<tr style="border-bottom: 1px solid #e5e7eb;">
                                                    <td style="padding: 12px 16px; color: #16a34a; font-size: 14px;">${d.name}</td>
                                                    <td style="padding: 12px 16px; text-align: right; color: #16a34a; font-size: 14px;">-$${d.amount.toFixed(2)}</td>
                                                  </tr>`
                                                ).join('')
                                              : ''}
                                            <tr style="background-color: #f9fafb;">
                                              <td style="padding: 16px; font-weight: 600; font-size: 16px; color: #1e3a8a;">Total</td>
                                              <td style="padding: 16px; text-align: right; font-weight: 600; font-size: 16px; color: #1e3a8a;">$${chargeBreakdown.total.toFixed(2)}</td>
                                            </tr>
                                          </table>
                                        </td>
                                      </tr>`;

                                      // Build payment status section - CHARGED (green success box)
                                      const paymentStatusSection = `<tr>
                                        <td style="padding: 0 40px 30px;">
                                          <div style="background: #dcfce7; border: 2px solid #16a34a; border-radius: 8px; padding: 20px; text-align: center;">
                                            <div style="font-size: 24px; margin-bottom: 8px;">✓</div>
                                            <div style="font-size: 18px; font-weight: 600; color: #16a34a;">Payment Complete</div>
                                            <div style="font-size: 14px; color: #374151; margin-top: 8px;">
                                              Your card was charged <strong>$${chargeBreakdown.total.toFixed(2)}</strong>. No balance due.
                                            </div>
                                          </div>
                                        </td>
                                      </tr>`;

                                      // Send service completion email directly from billing using send-notification
                                      // (billing has all the detailed data, trigger doesn't)
                                      console.log('📧 DEBUG - Service completion email data:', {
                                          serviceName: metadata.service_name,
                                          boatName,
                                          customerEmail: customer.email,
                                          hasBoatConditionsSection: !!boatConditionsSection,
                                          hasAnodeDetailsSection: !!anodeDetailsSection,
                                          hasPropellerSection: !!propellerSection,
                                          hasPricingBreakdown: !!pricingBreakdownHtml,
                                          hasPaymentStatus: !!paymentStatusSection
                                      });
                                      console.log('📧 DEBUG - Full email payload sections (first 200 chars each):', {
                                          boatConditionsSection: boatConditionsSection?.substring(0, 200) || 'EMPTY',
                                          anodeDetailsSection: anodeDetailsSection?.substring(0, 200) || 'EMPTY',
                                          propellerSection: propellerSection?.substring(0, 200) || 'EMPTY'
                                      });

                                      const emailResponse = await fetch('/api/send-notification', {
                                          method: 'POST',
                                          headers: {
                                              'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({
                                              type: 'service_completion',
                                              data: {
                                                  // Recipient info
                                                  customerEmail: customer.email,
                                                  customerName: customer.name,

                                                  // Service details
                                                  boatName: boatName,
                                                  serviceName: metadata.service_name,
                                                  serviceDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
                                                  totalHours: document.getElementById('wizardTotalHours')?.textContent || 'N/A',

                                                  // Invoice info
                                                  invoiceNumber: window.lastChargeInvoiceNumber || '',
                                                  invoiceTotal: '$' + chargeBreakdown.total.toFixed(2),

                                                  // Pre-built HTML sections (will be injected into template)
                                                  boatConditionsSection: boatConditionsSection,
                                                  anodeDetailsSection: anodeDetailsSection,
                                                  propellerSection: propellerSection,
                                                  pricingBreakdownHtml: pricingBreakdownHtml,
                                                  paymentStatusSection: paymentStatusSection
                                              },
                                              serviceName: 'billing'
                                          })
                                      }).then(r => r.json());

                                      if (emailResponse.error) {
                                          console.error('❌ Failed to send service completion email:', emailResponse.error);
                                      } else {
                                          console.log('✅ Service completion email sent successfully:', emailResponse.data);
                                      }
                                  } catch (error) {
                                      console.error('❌ Error sending service completion email:', error);
                                  }
                              })();

                          } else {
                              console.error('❌ Charge failed:', result);
                              // Only re-enable button on failure
                              chargeButton.disabled = false;
                              chargeButton.textContent = originalButtonText;
                              alert(`Charge failed: ${result.error || result.details || 'Unknown error'}`);
                          }

                      } catch (error) {
                          console.error('❌ Error charging customer:', error);
                          alert(`Error: ${error.message}`);

                          // Reset button
                          const chargeButton = document.getElementById('chargeButton');
                          chargeButton.disabled = false;
                          chargeButton.textContent = '💳 Charge Customer';
                      }

                  } else {
                      // No saved payment method - should not reach here if smartChargeOrInvoice routes correctly
                      console.error('⚠️ No saved payment method - charge flow requires payment method');
                      alert('Error: This customer does not have a payment method on file. Please use the invoice flow instead.');
                      return false;
                  }
              };

              console.log('✅ Enhanced charge flow with saved payment method support installed');
      }, 2000); // Wait for DOM to fully load
  });

  console.log('✅ Enhanced charge flow initialized');
}
